import type { Editor as EditorArg } from "tinymce";

export function onEditorKeypress(event: KeyboardEvent, editor: EditorArg) {
  // Only handle printable characters
  if (event.key.length !== 1 || event.ctrlKey || event.metaKey) return;

  const selection = editor.selection;
  const range = selection.getRng();

  if (!range.collapsed) return;

  const node = range.startContainer;
  const offset = range.startOffset;
  let shouldPreventDefault = false;
  let insertLocation: null | {
    type: "before" | "after";
    element: Element;
  } = null;

  const isUnderlined = (v: string) =>
    v.includes("sayas-") ||
    v.includes("lang-") ||
    v.includes("emphasis-") ||
    v.includes("rate-") ||
    v.includes("duration-") ||
    v.includes("volume-") ||
    v.includes("pitch-") ||
    v.includes("contour-");

  const isBracketLike = (v: string) =>
    v.includes("style-") || v.includes("phoneme-") || v.includes("voice-");

  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    const childNodes = Array.from(element.childNodes);

    // Caret is on the SSML span element itself
    if (element.hasAttribute("data-ssml")) {
      const ssmlValue = element.getAttribute("data-ssml") || "";
      if (isUnderlined(ssmlValue)) {
        const atStart = offset === 0;
        const atEnd = offset === childNodes.length;
        if (atStart || atEnd) {
          shouldPreventDefault = true;
          insertLocation = { type: atStart ? "before" : "after", element };
        }
      }
    }

    // Right before another SSML element
    if (!shouldPreventDefault && offset < childNodes.length) {
      const nextNode = childNodes[offset];
      if (nextNode.nodeType === Node.ELEMENT_NODE) {
        const nextElement = nextNode as Element;
        if (nextElement.hasAttribute("data-ssml")) {
          const v = nextElement.getAttribute("data-ssml") || "";
          if (isBracketLike(v) || isUnderlined(v)) {
            shouldPreventDefault = true;
            insertLocation = { type: "before", element: nextElement };
          }
        }
      }
    }

    // Right after another SSML element
    if (offset > 0 && !shouldPreventDefault) {
      const prevNode = childNodes[offset - 1];
      if (prevNode.nodeType === Node.ELEMENT_NODE) {
        const prevElement = prevNode as Element;
        if (prevElement.hasAttribute("data-ssml")) {
          const v = prevElement.getAttribute("data-ssml") || "";
          if (isBracketLike(v) || isUnderlined(v)) {
            shouldPreventDefault = true;
            insertLocation = { type: "after", element: prevElement };
          }
        }
      }
    }
  } else if (node.nodeType === Node.TEXT_NODE) {
    const parent = (node as Text).parentElement;
    if (parent && parent.hasAttribute("only-styling")) {
      // Typing inside opening/closing bracket
      const ssmlContainer = parent.closest("[data-ssml]") as Element | null;
      if (ssmlContainer) {
        const v = ssmlContainer.getAttribute("data-ssml") || "";
        if (isBracketLike(v)) {
          shouldPreventDefault = true;
          const textContent = parent.textContent || "";
          insertLocation =
            textContent === "{" || textContent.startsWith("[")
              ? { type: "before", element: ssmlContainer }
              : { type: "after", element: ssmlContainer };
        }
      }
    } else if (parent && parent.hasAttribute("data-ssml")) {
      // Typing at boundaries inside underlined tags
      const v = parent.getAttribute("data-ssml") || "";
      if (isUnderlined(v)) {
        const textContent = (node as Text).textContent || "";
        const isAtBeginning = offset === 0;
        const isAtEnd = offset === textContent.length;
        if (isAtBeginning || isAtEnd) {
          shouldPreventDefault = true;
          insertLocation = {
            type: isAtBeginning ? "before" : "after",
            element: parent,
          };
        }
      }
    }
  }

  if (shouldPreventDefault && insertLocation) {
    event.preventDefault();
    const textNode = document.createTextNode(event.key);
    if (insertLocation.type === "before") {
      insertLocation.element.parentNode?.insertBefore(
        textNode,
        insertLocation.element
      );
    } else {
      insertLocation.element.parentNode?.insertBefore(
        textNode,
        insertLocation.element.nextSibling
      );
    }
    const newRange = editor.dom.createRng();
    newRange.setStart(textNode, 1);
    newRange.setEnd(textNode, 1);
    selection.setRng(newRange);
    editor.fire("change");
  }
}
